/**
 * One error taxonomy for the whole client. Every failure path — HTTP status,
 * network drop, timeout, user abort, malformed body, or the soft `status:
 * "error"` body from `/query/test` — arrives at the UI as an `ApiError` with a
 * `kind` the UI can branch on and a message safe to show a user.
 */

export type ApiErrorKind =
  | 'network' // could not reach the server at all
  | 'timeout' // server accepted the request but did not answer in time
  | 'aborted' // superseded by a newer request, or cancelled by the user
  | 'unauthorized' // 401/403 — missing or rejected bearer token
  | 'not_found' // 404 — usually ENABLE_TEST_ROUTE=false
  | 'unavailable' // 503 — LLM/config not wired up
  | 'server' // 5xx
  | 'client' // other 4xx
  | 'malformed' // 2xx with a body we could not read
  | 'pipeline' // HTTP 200 but the query pipeline reported a failure

export interface ApiErrorOptions {
  kind: ApiErrorKind
  message: string
  status?: number
  code?: string
  detail?: unknown
  cause?: unknown
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | undefined
  readonly code: string | undefined
  readonly detail: unknown

  constructor(options: ApiErrorOptions) {
    super(options.message, { cause: options.cause })
    this.name = 'ApiError'
    this.kind = options.kind
    this.status = options.status
    this.code = options.code
    this.detail = options.detail
  }

  /** True when retrying the identical request could plausibly succeed. */
  get isRetryable(): boolean {
    return (
      this.kind === 'network' ||
      this.kind === 'timeout' ||
      this.kind === 'server' ||
      this.kind === 'unavailable'
    )
  }

  /** True when the user needs to change settings before anything will work. */
  get isConfiguration(): boolean {
    return (
      this.kind === 'unauthorized' ||
      this.kind === 'not_found' ||
      this.kind === 'unavailable'
    )
  }
}

/** Maps an HTTP status onto the taxonomy above. */
export function kindForStatus(status: number): ApiErrorKind {
  if (status === 401 || status === 403) return 'unauthorized'
  if (status === 404) return 'not_found'
  if (status === 503) return 'unavailable'
  if (status >= 500) return 'server'
  return 'client'
}

/**
 * Pulls a human-readable message out of whatever the backend put in the
 * `error` / `detail` field. FastAPI validation errors arrive as an array of
 * `{ loc, msg, type }`, HTTPExceptions as a string, and the LLM-config 503 as
 * `{ code, message }`.
 */
export function extractErrorMessage(payload: unknown, fallback: string): {
  message: string
  code?: string
} {
  if (payload == null) return { message: fallback }

  if (typeof payload === 'string') {
    return { message: payload.trim() || fallback }
  }

  if (Array.isArray(payload)) {
    const parts = payload
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && 'msg' in item) {
          const loc = 'loc' in item && Array.isArray(item.loc)
            ? `${item.loc.join('.')}: `
            : ''
          return `${loc}${String((item as { msg: unknown }).msg)}`
        }
        return null
      })
      .filter((part): part is string => Boolean(part))

    return parts.length ? { message: parts.join('; ') } : { message: fallback }
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    const nested = record.error ?? record.detail ?? record.message
    const code = typeof record.code === 'string' ? record.code : undefined

    if (nested != null && nested !== payload) {
      const inner = extractErrorMessage(nested, fallback)
      return { message: inner.message, code: code ?? inner.code }
    }

    if (code) return { message: fallback, code }
  }

  return { message: fallback }
}

/** Normalises anything thrown inside the client into an `ApiError`. */
export function toApiError(error: unknown, fallback: string): ApiError {
  if (error instanceof ApiError) return error

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError({
      kind: 'aborted',
      message: 'Request cancelled.',
      cause: error,
    })
  }

  if (error instanceof TypeError) {
    // `fetch` rejects with a TypeError for DNS failures, refused connections,
    // and CORS rejections alike — the browser deliberately hides which.
    return new ApiError({
      kind: 'network',
      message:
        'Could not reach the API. Check that the backend is running and that the base URL in Settings is correct.',
      cause: error,
    })
  }

  return new ApiError({
    kind: 'server',
    message: error instanceof Error ? error.message : fallback,
    cause: error,
  })
}
